import { formatCurrency, formatDate } from '@repo/shared-utils';
import type { Bill, Receivable } from '@repo/shared-types';
import type { ThreeWayMatch } from '@repo/shared-types/accounting/purchaseOrders';
import type { Inspection } from '@repo/shared-types/maintenance/inspections';
import type { HCVProgram } from '@repo/shared-types/compliance/schemas';
import type { GovernanceLogRecord } from '../../services/api/governance';
import { ROUTES } from '@repo/shared-types/routeRegistry';

/** Aligns with GET /accounting/periods/financial-blockers (month-end seal). */
export type FinancialCloseBlockerLike = {
  code: string;
  severity: 'Critical' | 'High';
  message: string;
  route?: string;
};

export type ExceptionQueueItem = {
  id: string;
  partition: 'financial' | 'operational' | 'system_health';
  title: string;
  detail: string;
  severity: 'Critical' | 'High' | 'Medium';
  route: string;
  /** Financial items must be cleared in source systems — not via governance “Resolve”. */
  resolvableViaGovernance: boolean;
  /** Present for system health / integrity probes (P2 acknowledge path). */
  findingType?: string;
  fingerprint?: string;
  entityId?: string;
  entityType?: string;
};

const FINANCIAL_CODES_FROM_LISTS = new Set([
  'bill_overdue',
  'receivable_late',
  'receivable_unpaid',
  'three_way_discrepancy',
]);

export function buildFinancialItemsFromAccountingLists(params: {
  bills: Bill[];
  receivables: Receivable[];
  matches: ThreeWayMatch[];
}): ExceptionQueueItem[] {
  const { bills, receivables, matches } = params;
  const out: ExceptionQueueItem[] = [];

  for (const bill of bills) {
    if (bill.status !== 'Overdue') continue;
    out.push({
      id: `bill-${bill.id}`,
      partition: 'financial',
      title: 'Overdue bill requires payment decision',
      detail: `${formatCurrency(bill.amountCents)} · due ${formatDate(bill.dueDate, 'short')}`,
      severity: 'High',
      route: `${ROUTES.BILLS}?billId=${bill.id}`,
      resolvableViaGovernance: false,
    });
  }

  for (const receivable of receivables) {
    if (receivable.status !== 'Late' && receivable.status !== 'Unpaid') continue;
    const isPhaRent =
      receivable.type === 'Rent' && receivable.rentObligationParty === 'SUBSIDY_PHA';
    out.push({
      id: `receivable-${receivable.id}`,
      partition: 'financial',
      title: isPhaRent ? 'Pending subsidy (PHA / HAP)' : 'Delinquency escalation needed',
      detail: isPhaRent
        ? `${receivable.status} · PHA portion ${formatCurrency(receivable.amountCents ?? 0)} (subsidy settlement)`
        : `${receivable.status} · ${formatCurrency(receivable.amountCents ?? 0)}`,
      severity: receivable.status === 'Late' ? 'Critical' : 'High',
      route: `${ROUTES.RECEIVABLES}?receivableId=${receivable.id}`,
      resolvableViaGovernance: false,
    });
  }

  for (const match of matches) {
    if (match.status !== 'discrepancy') continue;
    out.push({
      id: `match-${match.id}`,
      partition: 'financial',
      title: 'Three-way match discrepancy',
      detail: `${match.discrepancies?.length ?? 0} discrepancy item(s)`,
      severity: 'Critical',
      route: `${ROUTES.ACCOUNTING_THREE_WAY_MATCH}?matchId=${match.id}`,
      resolvableViaGovernance: false,
    });
  }

  return out;
}

/** Extra Financial blockers returned only by the server (payouts, transfers, Plaid, integrations). */
export function buildFinancialItemsFromServerOnly(blockers: FinancialCloseBlockerLike[]): ExceptionQueueItem[] {
  return blockers
    .filter((b) => !FINANCIAL_CODES_FROM_LISTS.has(b.code))
    .map((b, idx) => ({
      id: `financial-server-${b.code}-${idx}`,
      partition: 'financial' as const,
      title: b.message,
      detail: `Code: ${b.code}`,
      severity: b.severity,
      route: b.route ?? ROUTES.CLOSE,
      resolvableViaGovernance: false,
    }));
}

export function buildOperationalItems(params: {
  inspections: Inspection[];
  hcvPrograms: HCVProgram[];
  governanceLogs: GovernanceLogRecord[];
}): ExceptionQueueItem[] {
  const { inspections, hcvPrograms, governanceLogs } = params;
  const out: ExceptionQueueItem[] = [];

  for (const inspection of inspections) {
    if (inspection.status !== 'Flagged') continue;
    out.push({
      id: `inspection-${inspection.id}`,
      partition: 'operational',
      title: 'Flagged inspection findings',
      detail: `${inspection.type} · ${inspection.inspector}`,
      severity: 'Critical',
      route: `${ROUTES.INSPECTIONS}?inspectionId=${inspection.id}`,
      resolvableViaGovernance: true,
    });
  }

  for (const program of hcvPrograms) {
    if (program.complianceStatus !== 'At Risk') continue;
    out.push({
      id: `hcv-${program.id}`,
      partition: 'operational',
      title: 'HCV / compliance at risk',
      detail: `${program.phaName} · voucher ${program.voucherNumber ?? 'N/A'}`,
      severity: 'High',
      route: '/app/compliance/hcv',
      resolvableViaGovernance: true,
    });
  }

  for (const log of governanceLogs) {
    if (log.type === 'ExceptionResolution') continue;
    if (log.status !== 'Warning' && log.status !== 'Error') continue;
    out.push({
      id: `governance-${log.id}`,
      partition: 'operational',
      title: 'High-severity governance event',
      detail: `${log.type} · ${log.title}`,
      severity: 'High',
      route: `/app/compliance/governance?logId=${log.id}`,
      resolvableViaGovernance: true,
    });
  }

  return out;
}

/** Maps API system-health DTOs into the shared exception queue row shape. */
export function buildSystemHealthExceptionQueueItems(
  rows: Array<{
    id: string;
    partition: 'system_health';
    title: string;
    detail: string;
    severity: 'Critical' | 'High' | 'Medium';
    route: string;
    resolvableViaGovernance: boolean;
    findingType: string;
    fingerprint: string;
    entityId: string;
    entityType: string;
  }>,
): ExceptionQueueItem[] {
  return rows.map((r) => ({
    id: r.id,
    partition: 'system_health',
    title: r.title,
    detail: r.detail,
    severity: r.severity,
    route: r.route,
    resolvableViaGovernance: r.resolvableViaGovernance,
    findingType: r.findingType,
    fingerprint: r.fingerprint,
    entityId: r.entityId,
    entityType: r.entityType,
  }));
}

export type OpenExceptionQueuePartitions = {
  financial: ExceptionQueueItem[];
  operational: ExceptionQueueItem[];
  systemHealth: ExceptionQueueItem[];
};

export type OpenExceptionQueueCountInput = {
  bills: Bill[];
  receivables: Receivable[];
  matches: ThreeWayMatch[];
  financialBlockers: FinancialCloseBlockerLike[];
  inspections: Inspection[];
  hcvPrograms: HCVProgram[];
  governanceLogs: GovernanceLogRecord[];
  systemHealthRows?: Parameters<typeof buildSystemHealthExceptionQueueItems>[0];
};

function buildResolvedExceptionEntityIds(governanceLogs: GovernanceLogRecord[]): Set<string> {
  return new Set(
    governanceLogs.filter((l) => l.type === 'ExceptionResolution' && l.entityId).map((l) => l.entityId as string),
  );
}

/** Single source of truth for open exception queue rows (Dashboard + ExceptionQueueView). */
export function buildOpenExceptionQueuePartitions(input: OpenExceptionQueueCountInput): OpenExceptionQueuePartitions {
  const resolvedIds = buildResolvedExceptionEntityIds(input.governanceLogs);
  const financial = [
    ...buildFinancialItemsFromAccountingLists({
      bills: input.bills,
      receivables: input.receivables,
      matches: input.matches,
    }),
    ...buildFinancialItemsFromServerOnly(input.financialBlockers),
  ].filter((item) => !resolvedIds.has(item.id));
  const operational = buildOperationalItems({
    inspections: input.inspections,
    hcvPrograms: input.hcvPrograms,
    governanceLogs: input.governanceLogs,
  }).filter((item) => !resolvedIds.has(item.id));
  const systemHealth = buildSystemHealthExceptionQueueItems(input.systemHealthRows ?? []).filter(
    (item) => !resolvedIds.has(item.id),
  );
  return { financial, operational, systemHealth };
}

export function countOpenExceptions(partitions: OpenExceptionQueuePartitions): {
  financial: number;
  operational: number;
  systemHealth: number;
  total: number;
} {
  const financial = partitions.financial.length;
  const operational = partitions.operational.length;
  const systemHealth = partitions.systemHealth.length;
  return { financial, operational, systemHealth, total: financial + operational + systemHealth };
}
